import { Suspense, useEffect, type ReactElement } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'

import { OrgChoicePage } from '@nebula/shared/components/OrgChoicePage'
import { useAuthStore } from '@nebula/shared/store'
import { lazyWithRetry } from '@nebula/shared/utils'

import LoginPage from './pages/LoginPage'

const MainLayout = lazyWithRetry(() => import('./layouts/MainLayout'))
const VerifyEmailPage = lazyWithRetry(() => import('./pages/VerifyEmailPage'))
const DashboardPage = lazyWithRetry(() => import('./pages/DashboardPage'))
const ClustersPage = lazyWithRetry(() => import('./pages/ClustersPage'))
const ClusterDetailPage = lazyWithRetry(() => import('./pages/ClusterDetailPage'))
const ClusterSettingsPage = lazyWithRetry(() => import('./pages/ClusterSettingsPage'))
const NetworkPoolsPage = lazyWithRetry(() => import('./pages/NetworkPoolsPage'))
const PublicIPPoolPage = lazyWithRetry(() => import('./pages/PublicIPPoolPage'))
const ProductsPage = lazyWithRetry(() => import('./pages/ProductsPage'))
const ZoneConfigurationPage = lazyWithRetry(() => import('./pages/ZoneConfigurationPage'))
const InventoryPage = lazyWithRetry(() => import('./pages/InventoryPage'))
const PBSServerPage = lazyWithRetry(() => import('./pages/PBSServerPage'))
const BackupAnalyticsPage = lazyWithRetry(() => import('./pages/BackupAnalyticsPage'))
const NetworkTopologyPage = lazyWithRetry(() => import('./pages/NetworkTopologyPage'))
const SDNZonesPage = lazyWithRetry(() => import('./pages/SDNZonesPage'))
const SDNVNETsPage = lazyWithRetry(() => import('./pages/SDNVNETsPage'))
const TeamPage = lazyWithRetry(() => import('./pages/TeamPage'))
const SystemLogsPage = lazyWithRetry(() => import('./pages/SystemLogsPage'))
const OrganizationsPage = lazyWithRetry(() => import('./pages/OrganizationsPage'))
const SupportPage = lazyWithRetry(() => import('./pages/SupportPage'))
const AccountPage = lazyWithRetry(() => import('./pages/AccountPage'))
const NotificationsPage = lazyWithRetry(() => import('./pages/NotificationsPage'))
const InvoicesPage = lazyWithRetry(() => import('./pages/InvoicesPage'))
const TitulosPage = lazyWithRetry(() => import('./pages/TitulosPage'))
const InviteAcceptPage = lazyWithRetry(() =>
  import('@nebula/shared/components').then((module) => ({ default: module.InviteAcceptPage })),
)

function RouteLoadingFallback() {
  return (
    <div className="flex min-h-[40vh] items-center justify-center">
      <div className="text-center">
        <div className="mx-auto mb-3 size-7 animate-spin rounded-full border-b-2 border-blue-600" />
        <p className="text-sm text-gray-600">Carregando página...</p>
      </div>
    </div>
  )
}

function withRouteFallback(element: ReactElement) {
  return <Suspense fallback={<RouteLoadingFallback />}>{element}</Suspense>
}

function App() {
  const { user, token, checkAuth, isAuthenticated, isLoading, orgChoiceRequired } = useAuthStore()
  const location = useLocation()
  const isLoginPage = location.pathname === '/login' || location.pathname === '/'

  useEffect(() => {
    checkAuth()
  }, [checkAuth])

  // Redirect após login se veio de convite
  useEffect(() => {
    if (user) {
      const pendingRedirect = sessionStorage.getItem('invite_redirect')
      if (pendingRedirect) {
        sessionStorage.removeItem('invite_redirect')
        window.location.replace(pendingRedirect)
      }
    }
  }, [user])

  // Show loading while checking auth (but NOT on login page to avoid remounting).
  // Só com token: sem ele o isLoading é o do login() em andamento, e trocar a LoginPage
  // pelo spinner a remontaria numa rota interna, perdendo o modal do código de 2FA (#1171)
  if (isLoading && !isLoginPage && token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="mx-auto mb-4 size-8 animate-spin rounded-full border-b-2 border-blue-600"></div>
          <p className="text-gray-600">Carregando...</p>
        </div>
      </div>
    )
  }

  // If not authenticated or no user, show login or public routes
  if (!isAuthenticated || !user) {
    // Allow public routes without authentication
    if (location.pathname === '/verify-email' || location.pathname.startsWith('/invite/')) {
      return (
        <Routes>
          <Route path="/verify-email" element={withRouteFallback(<VerifyEmailPage />)} />
          <Route path="/invite/:token" element={withRouteFallback(<InviteAcceptPage />)} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      )
    }

    return <LoginPage />
  }

  // Mais de uma org compatível e a aba não escolheu: pergunta antes de qualquer tela (#1145)
  if (orgChoiceRequired) {
    return <OrgChoicePage />
  }

  // Verificar tipo de organização (organization.type reflete a org ativa selecionada pelo auth store)
  const organizationType = user?.organization?.type || user?.organization_type
  const hasAccess = organizationType === 'cloud_provider'

  if (!hasAccess) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-100">
        <div className="text-center">
          <h1 className="mb-2 text-2xl font-bold text-blue-600">Acesso Negado</h1>
          <p className="mb-4 text-gray-600">Esta aplicação é restrita a Cloud Providers</p>
          <p className="mb-4 text-sm text-gray-500">
            Tipo da organização: {organizationType || 'Não definido'}
          </p>
          <button
            onClick={() => useAuthStore.getState().logout()}
            className="rounded-lg bg-gray-600 px-4 py-2 text-white hover:bg-gray-700"
          >
            Fazer Logout
          </button>
        </div>
      </div>
    )
  }

  return (
    <Routes>
      {/* Invite accept page - outside MainLayout */}
      <Route path="/invite/:token" element={withRouteFallback(<InviteAcceptPage />)} />
      <Route path="/login" element={<Navigate to="/dashboard" replace />} />
      <Route path="/" element={withRouteFallback(<MainLayout />)}>
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={withRouteFallback(<DashboardPage />)} />
        <Route path="clusters" element={withRouteFallback(<ClustersPage />)} />
        <Route path="clusters/:clusterId" element={withRouteFallback(<ClusterDetailPage />)} />
        <Route
          path="clusters/:clusterId/settings"
          element={withRouteFallback(<ClusterSettingsPage />)}
        />
        <Route path="network-pools" element={withRouteFallback(<NetworkPoolsPage />)} />
        <Route
          path="network-pools/:poolId/public-ips"
          element={withRouteFallback(<PublicIPPoolPage />)}
        />
        <Route path="network-topology" element={withRouteFallback(<NetworkTopologyPage />)} />
        <Route path="sdn-zones" element={withRouteFallback(<SDNZonesPage />)} />
        <Route path="sdn-vnets" element={withRouteFallback(<SDNVNETsPage />)} />
        <Route path="products" element={withRouteFallback(<ProductsPage />)} />
        <Route path="products/zones" element={withRouteFallback(<ZoneConfigurationPage />)} />
        <Route path="invoices" element={withRouteFallback(<InvoicesPage />)} />
        <Route path="titulos" element={withRouteFallback(<TitulosPage />)} />
        <Route path="titulos/:tituloId" element={withRouteFallback(<TitulosPage />)} />
        <Route path="team" element={withRouteFallback(<TeamPage />)} />
        <Route path="support" element={withRouteFallback(<SupportPage />)} />
        <Route path="system-logs" element={withRouteFallback(<SystemLogsPage />)} />
        <Route path="organizations" element={withRouteFallback(<OrganizationsPage />)} />
        <Route path="inventory" element={withRouteFallback(<InventoryPage />)} />
        <Route path="pbs-servers" element={withRouteFallback(<PBSServerPage />)} />
        <Route path="backup-analytics" element={withRouteFallback(<BackupAnalyticsPage />)} />
        <Route path="profile" element={withRouteFallback(<AccountPage />)} />
        <Route path="notifications" element={withRouteFallback(<NotificationsPage />)} />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  )
}

export default App
